import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Container, Box, CircularProgress } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { SignIn } from '@clerk/clerk-react';
import { useAuth } from './hooks/useAuth';
import { useInactivityTimeout } from './hooks/useInactivityTimeout';
import { getInactivityTimeout, SECURITY_CONFIG } from './config/security';

// Components
import Header from './components/Header';
import Dashboard from './pages/Dashboard';
import TestRunner from './pages/TestRunner';
import TestHistory from './pages/TestHistory';
import DeviceManager from './pages/DeviceManager';
import DeviceRegistration from './pages/DeviceRegistration';
import APKVersionManagerNew from './pages/APKVersionManagerNew';
import PostReleaseTesting from './pages/PostReleaseTesting';
import APKTestPage from './pages/APKTestPage';
import DeviceMonitor from './pages/DeviceMonitor';
import AccountManager from './pages/AccountManager';
import SQLValidation from './pages/SQLValidation';
import ConcurrencyTestingPanel from './pages/ConcurrencyTestingPanel';
import QueueManagement from './pages/QueueManagement';
import TestConfigManagement from './pages/TestConfigManagement';
import CashOnlyControl from './pages/CashOnlyControl';
import CashOnlyDevicePool from './pages/CashOnlyDevicePool';
import CashOnlyDashboardPublic from './pages/CashOnlyDashboardPublic';

// Create responsive theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    // 响应式标题
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      '@media (min-width:600px)': {
        fontSize: '1.75rem',
      },
      '@media (min-width:960px)': {
        fontSize: '2.125rem',
      },
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      '@media (min-width:600px)': {
        fontSize: '1.4rem',
      },
      '@media (min-width:960px)': {
        fontSize: '1.5rem',
      },
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      '@media (min-width:600px)': {
        fontSize: '1.1rem',
      },
      '@media (min-width:960px)': {
        fontSize: '1.25rem',
      },
    },
    body1: {
      fontSize: '0.875rem',
      '@media (min-width:600px)': {
        fontSize: '1rem',
      },
    },
    body2: {
      fontSize: '0.75rem',
      '@media (min-width:600px)': {
        fontSize: '0.875rem',
      },
    },
  },
  components: {
    // 响应式按钮
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', // 不强制大写
          '@media (max-width:600px)': {
            fontSize: '0.75rem',
            padding: '6px 12px',
          },
        },
        sizeLarge: {
          '@media (max-width:600px)': {
            fontSize: '0.875rem',
            padding: '8px 16px',
          },
        },
      },
    },
    // 响应式卡片
    MuiCard: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            borderRadius: 8,
          },
        },
      },
    },
    // 响应式芯片
    MuiChip: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            fontSize: '0.7rem',
            height: 24,
          },
        },
      },
    },
  },
});

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0, // 立即过期，强制刷新
      refetchOnWindowFocus: true, // 窗口聚焦时刷新
    },
  },
});

// 🔐 Sign In Page Component (防止登录后循环 + 支持登录后重定向)
function SignInPage() {
  const { isLoaded, isSignedIn } = useAuth();

  // 如果还在加载，显示loading
  if (!isLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // 🎯 如果已登录，重定向到原始目标页面（支持从 Slack 链接直接访问）
  if (isSignedIn) {
    // 尝试从 sessionStorage 读取重定向URL
    let redirectPath = '/';
    try {
      const savedRedirect = sessionStorage.getItem('clerk_redirect_url');
      if (savedRedirect && savedRedirect !== '/sign-in') {
        redirectPath = savedRedirect;
        // 清除已使用的重定向URL
        sessionStorage.removeItem('clerk_redirect_url');
        console.log('🔄 登录成功，重定向到:', redirectPath);
      }
    } catch (e) {
      console.warn('读取重定向URL失败:', e);
    }
    
    return <Navigate to={redirectPath} replace />;
  }

  // 🎯 未登录，显示登录界面（设置登录后的重定向URL）
  const getRedirectUrl = () => {
    try {
      const savedRedirect = sessionStorage.getItem('clerk_redirect_url');
      if (savedRedirect && savedRedirect !== '/sign-in') {
        return `/#${savedRedirect}`;
      }
    } catch (e) {
      console.warn('读取重定向URL失败:', e);
    }
    return '/#/';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <SignIn 
        routing="hash"
        signUpUrl="/#/sign-in"
        forceRedirectUrl={getRedirectUrl()}
      />
    </Box>
  );
}

// 🔒 Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  
  // 🎯 不活跃超时：配置的时间无操作自动登出
  useInactivityTimeout(
    getInactivityTimeout(), 
    isSignedIn && SECURITY_CONFIG.ENABLE_INACTIVITY_TIMEOUT
  );

  if (!isLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isSignedIn) {
    // 🎯 保存当前URL，登录后跳转回来
    const currentPath = window.location.hash.substring(1); // 去掉 # 号
    const redirectUrl = currentPath || '/';
    
    // 保存到 sessionStorage
    try {
      sessionStorage.setItem('clerk_redirect_url', redirectUrl);
    } catch (e) {
      console.warn('无法保存重定向URL:', e);
    }
    
    return <Navigate to="/sign-in" replace />;
  }

  return (
    <>
      <Header />
      <Container 
        maxWidth="xl" 
        sx={{ 
          mt: { xs: 2, sm: 3, md: 4 }, 
          mb: { xs: 2, sm: 3, md: 4 }, 
          px: { xs: 1, sm: 2, md: 3 },
          flex: 1,
        }}
      >
        {children}
      </Container>
    </>
  );
}

function App() {
  // 处理被错误前缀（如 /apks/...）包裹的 URL，自动跳转到 Hash 路由
  React.useEffect(() => {
    try {
      const { pathname, hash, origin } = window.location;
      // 只要带有 hash 路由且 pathname 不是根路径，就矫正为 {origin}/#/{route}
      if (hash && hash.startsWith('#/') && pathname !== '/' && pathname !== '') {
        window.history.replaceState(null, '', `${origin}/${hash}`);
      }
    } catch {}
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider maxSnack={3}>
          <Router>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Routes>
                {/* 🔐 Public route: Sign In page */}
                <Route
                  path="/sign-in/*"
                  element={<SignInPage />}
                />
                <Route path="/public/cash-only-dashboard" element={<CashOnlyDashboardPublic />} />

                {/* 🔒 Protected routes: Require authentication */}
                <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/test-runner" element={<ProtectedRoute><TestRunner /></ProtectedRoute>} />
                <Route path="/history" element={<ProtectedRoute><TestHistory /></ProtectedRoute>} />
                <Route path="/devices" element={<ProtectedRoute><DeviceManager /></ProtectedRoute>} />
                <Route path="/register-device" element={<ProtectedRoute><DeviceRegistration /></ProtectedRoute>} />
                <Route path="/apk-versions" element={<ProtectedRoute><APKVersionManagerNew /></ProtectedRoute>} />
                <Route path="/post-release-testing" element={<ProtectedRoute><PostReleaseTesting /></ProtectedRoute>} />
                <Route path="/apk-test" element={<ProtectedRoute><APKTestPage /></ProtectedRoute>} />
                <Route path="/account-manager" element={<ProtectedRoute><AccountManager /></ProtectedRoute>} />
                <Route path="/sql-validation" element={<ProtectedRoute><SQLValidation /></ProtectedRoute>} />
                <Route path="/device-monitor" element={<ProtectedRoute><DeviceMonitor /></ProtectedRoute>} />
                <Route path="/concurrency-testing" element={<ProtectedRoute><ConcurrencyTestingPanel /></ProtectedRoute>} />
                <Route path="/queue-management" element={<ProtectedRoute><QueueManagement /></ProtectedRoute>} />
                <Route path="/test-configs" element={<ProtectedRoute><TestConfigManagement /></ProtectedRoute>} />
                <Route path="/cash-only" element={<ProtectedRoute><CashOnlyControl /></ProtectedRoute>} />
                <Route path="/cash-only-device-pool" element={<ProtectedRoute><CashOnlyDevicePool /></ProtectedRoute>} />
              </Routes>
            </Box>
          </Router>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
